import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus, Search, MoreVertical, FileText, Copy, Trash2,
  Eye, Pencil, TrendingUp, CheckCircle, Clock, Send, Users, Package,
} from 'lucide-react';
import { EstadoBadge } from '@/components/EstadoBadge';
import { AppLayout } from '@/components/AppLayout';
import { usePresupuestos } from '@/hooks/useData';
import { formatEUR, formatFecha, calcularTotales } from '@/lib/calc';
import type { EstadoPresupuesto, Presupuesto } from '@/lib/types';

type Filtro = 'Todos' | EstadoPresupuesto;

export default function Dashboard() {
  const navigate = useNavigate();
  const { lista, crear, eliminar, duplicar, guardar } = usePresupuestos();
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('Todos');
  const [toDelete, setToDelete] = useState<string | null>(null);

  const filtrados = lista
    .filter(p => filtro === 'Todos' || p.estado === filtro)
    .filter(p => {
      if (!busqueda) return true;
      const q = busqueda.toLowerCase();
      return (
        p.numero.toLowerCase().includes(q) ||
        p.clienteNombre.toLowerCase().includes(q) ||
        p.aseguradora.toLowerCase().includes(q) ||
        p.numeroSiniestro.toLowerCase().includes(q)
      );
    });

  const handleNuevo = () => { const p = crear(); navigate(`/editor/${p.id}`); };
  const handleDuplicar = (id: string) => { const p = duplicar(id); navigate(`/editor/${p.id}`); };

  const kpis = [
    { label: 'Presupuestos', value: lista.length, icon: FileText, color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'Enviados', value: lista.filter(p => p.estado === 'Enviado').length, icon: Send, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Aceptados', value: lista.filter(p => p.estado === 'Aceptado').length, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Borradores', value: lista.filter(p => p.estado === 'Borrador').length, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    {
      label: 'Facturado',
      value: formatEUR(lista.filter(p => p.estado === 'Aceptado').reduce((s, p) => s + calcularTotales(p.lineas).totalGeneral, 0)),
      icon: TrendingUp, color: 'text-primary', bg: 'bg-primary/10',
    },
  ];

  return (
    <AppLayout title="Presupuestos" active="presupuestos">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {kpis.map(({ label, value, icon: Icon, color, bg }) => (
            <motion.div key={label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-xl p-4"
              style={{ boxShadow: '0 2px 8px -4px color-mix(in srgb, var(--primary) 6%, transparent)' }}
            >
              <div className={`${bg} ${color} h-7 w-7 rounded-md flex items-center justify-center mb-2`}>
                <Icon size={15} />
              </div>
              <p className={`font-bold text-lg tracking-tight font-mono ${color}`}>{value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            </motion.div>
          ))}
        </div>

        {/* Accesos rápidos a nuevos módulos */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/clientes')}
            className="flex items-center gap-3 bg-card border border-border rounded-xl p-4 hover:border-primary/40 hover:bg-muted/30 transition-all text-left group"
          >
            <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
              <Users size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-sm">Base de clientes</p>
              <p className="text-xs text-muted-foreground">Gestiona particulares, empresas y aseguradoras</p>
            </div>
          </button>
          <button
            onClick={() => navigate('/productos')}
            className="flex items-center gap-3 bg-card border border-border rounded-xl p-4 hover:border-primary/40 hover:bg-muted/30 transition-all text-left group"
          >
            <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0 group-hover:bg-emerald-100 transition-colors">
              <Package size={18} className="text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-sm">Catálogo de productos y servicios</p>
              <p className="text-xs text-muted-foreground">Precios estándar reutilizables en cada presupuesto</p>
            </div>
          </button>
        </div>

        {/* Filtros + Nuevo */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Buscar por nº, cliente, aseguradora o siniestro…" value={busqueda} onChange={e => setBusqueda(e.target.value)} className="pl-9" />
          </div>
          <Select value={filtro} onValueChange={v => setFiltro(v as Filtro)}>
            <SelectTrigger className="w-full sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Todos">Todos</SelectItem>
              <SelectItem value="Borrador">Borrador</SelectItem>
              <SelectItem value="Enviado">Enviado</SelectItem>
              <SelectItem value="Aceptado">Aceptado</SelectItem>
              <SelectItem value="Rechazado">Rechazado</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleNuevo} className="gap-1.5 shrink-0"><Plus size={16} /> Nuevo</Button>
        </div>

        {/* Lista de presupuestos */}
        {filtrados.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center py-20 text-center">
            <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <FileText size={28} className="text-muted-foreground" />
            </div>
            <p className="font-semibold mb-1">{busqueda || filtro !== 'Todos' ? 'Sin resultados' : 'Aún no hay presupuestos'}</p>
            <p className="text-sm text-muted-foreground mb-4">{busqueda || filtro !== 'Todos' ? 'Prueba con otros filtros' : 'Crea tu primer presupuesto'}</p>
            {!busqueda && filtro === 'Todos' && <Button onClick={handleNuevo} className="gap-1.5"><Plus size={16} /> Nuevo presupuesto</Button>}
          </motion.div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="hidden md:grid grid-cols-[120px_1fr_160px_100px_120px_140px_44px] gap-3 px-4 py-2 border-b border-border bg-muted/40">
              {['Número', 'Cliente', 'Aseguradora / Siniestro', 'Fecha', 'Estado', 'Total', ''].map(h => (
                <span key={h} className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{h}</span>
              ))}
            </div>
            <AnimatePresence>
              {filtrados.map((p, i) => (
                <PresupuestoRow
                  key={p.id}
                  presupuesto={p}
                  isLast={i === filtrados.length - 1}
                  onView={() => navigate(`/preview/${p.id}`)}
                  onEdit={() => navigate(`/editor/${p.id}`)}
                  onDuplicate={() => handleDuplicar(p.id)}
                  onDelete={() => setToDelete(p.id)}
                  onEstado={estado => guardar({ ...p, estado })}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar presupuesto?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (toDelete) eliminar(toDelete); setToDelete(null); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function PresupuestoRow({ presupuesto: p, isLast, onView, onEdit, onDuplicate, onDelete, onEstado }: {
  presupuesto: Presupuesto; isLast: boolean;
  onView: () => void; onEdit: () => void; onDuplicate: () => void;
  onDelete: () => void; onEstado: (e: EstadoPresupuesto) => void;
}) {
  const t = calcularTotales(p.lineas);
  return (
    <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className={`grid grid-cols-1 md:grid-cols-[120px_1fr_160px_100px_120px_140px_44px] gap-3 items-center px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer ${!isLast ? 'border-b border-border' : ''}`}
      onClick={onView}
    >
      <span className="font-mono text-xs font-semibold text-primary">{p.numero}</span>
      <span className="font-medium text-sm truncate">{p.clienteNombre || <span className="text-muted-foreground italic">Sin cliente</span>}</span>
      <div className="text-xs text-muted-foreground truncate">
        {p.aseguradora && <span className="font-medium text-foreground">{p.aseguradora}</span>}
        {p.aseguradora && p.numeroSiniestro && ' · '}
        {p.numeroSiniestro && <span className="font-mono">{p.numeroSiniestro}</span>}
        {!p.aseguradora && !p.numeroSiniestro && '—'}
      </div>
      <span className="text-xs text-muted-foreground">{formatFecha(p.fecha)}</span>
      <EstadoBadge estado={p.estado} />
      <span className="font-mono font-bold text-sm text-right">{formatEUR(t.totalGeneral)}</span>
      <div className="flex justify-end" onClick={e => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical size={15} /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={onView}><Eye size={14} className="mr-2" /> Ver / Imprimir PDF</DropdownMenuItem>
            <DropdownMenuItem onClick={onEdit}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate}><Copy size={14} className="mr-2" /> Duplicar</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onEstado('Enviado')} className="text-blue-600"><Send size={14} className="mr-2" /> Marcar enviado</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEstado('Aceptado')} className="text-emerald-600"><CheckCircle size={14} className="mr-2" /> Marcar aceptado</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive"><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </motion.div>
  );
}
